import React from 'react';

import { formatMoney } from '@betterment/core';
import { Box, ListRow, Text, Tooltip } from '@betterment/samba-react';

import { useFallbackKeys } from '../../hooks/useFallbackKeys';

import type { IraContributionYear, IraContributionYears, IraMaxOutStatuses } from './data';

const IraLimitMaxOutProjection = (iraContribution: IraContributionYear): IraMaxOutStatuses => {
  const contributionLimit = iraContribution.taxYear.iraContributionLimit.cents;
  const currentContributionAmount = iraContribution.result.amount.cents;
  const projectedContributionAmount = iraContribution.result.projection?.total?.cents || 0;

  if (currentContributionAmount >= contributionLimit) {
    return 'Maxed';
  }
  if (projectedContributionAmount >= contributionLimit) {
    return 'ProjectedToMax';
  }
  return 'NotProjectedToMax';
};

const IraLimitTooltipCopy = (iraContribution: IraContributionYear): string => {
  if (iraContribution.taxYear.isEligibleForIraContributionCatchUp) {
    return 'Limit includes maximum standard contribution amount as well as catch-up contributions for individuals 50 and over.';
  }
  return 'Limit includes maximum standard contribution amount.';
};

export const IraContributionSummaries: React.FC<{ iraContributions: IraContributionYears }> = ({
  iraContributions,
}) => {
  const contributionsByYear = useFallbackKeys(iraContributions);

  return (
    <>
      {contributionsByYear.map(({ item: iraContribution, key }) => {
        const status = IraLimitMaxOutProjection(iraContribution);
        const IraContributionTooltipLimitCopy = IraLimitTooltipCopy(iraContribution);
        return (
          <ListRow key={key} css={{ padding: 'space.25' }}>
            <ListRow.LeadingContent css={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box css={{ display: 'flex', flexDirection: 'row' }}>
                <Text>{iraContribution.taxYear.year} contributions</Text>
                <Tooltip>
                  <Tooltip.Trigger asChild={true} style={{ paddingLeft: '4px' }}>
                    <Tooltip.IconButton
                      aria-label={`${iraContribution.taxYear.year} contributions tooltip`}
                      name="InfoCircle"
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Content align="start" side="right">
                    {IraContributionTooltipLimitCopy}
                  </Tooltip.Content>
                </Tooltip>
              </Box>
              {status === 'Maxed' && (
                <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
                  Maxed out
                </Text>
              )}
              {status === 'ProjectedToMax' && (
                <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
                  Projected to max out
                </Text>
              )}
              {status === 'NotProjectedToMax' && (
                <Text css={{ textAlign: 'left' }} color="text.weak" variant="text50">
                  Limit: {formatMoney(iraContribution.taxYear.iraContributionLimit)}
                </Text>
              )}
            </ListRow.LeadingContent>
            <ListRow.TrailingContent>
              <Text css={{ textAlign: 'right' }}>{formatMoney(iraContribution.result.amount)}</Text>
            </ListRow.TrailingContent>
          </ListRow>
        );
      })}
    </>
  );
};
